import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RowPreviewComponent } from '../../partials/row-preview/row-preview.component';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import { BroadcastMessageService } from '../../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../../services/broadcast/types/broadcastTypes';
import { removeMessageAttribute } from '../../../../../../utils/utils';
import { describeChar, formatCount } from '../../../../../../utils/sms';

/** Fields shown before "Show all". */
const FIELD_PREVIEW = 10;

/**
 * Step 3 — write the message. Fields come from the sheet's columns, so each
 * `{{field}}` maps to real data. The preview steps through the rows and shows
 * exactly what each person gets, empty values and all.
 */
import { BkSelectComponent } from '../../../../../components/inputs/bk-select/bk-select.component';
@Component({
  selector: 'sb-compose',
  standalone: true,
  imports: [RowPreviewComponent, MatTooltipModule, BkSelectComponent],
  templateUrl: './compose.component.html',
  styleUrls: ['../../sb-shared.css', './compose.component.css'],
})
export class ComposeComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  store = inject(SmartBroadcastService);
  broadcastService = inject(BroadcastMessageService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('bodyArea') bodyArea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('usedList') usedList?: ElementRef<HTMLElement>;

  senderIDs: ISenderID[] = [];
  senderLoading = false;
  /** The sender ID request failed; it is tried again when the step reopens. */
  senderFailed = false;
  nameTouched = false;
  showAllFields = false;
  private cursorTimeout?: ReturnType<typeof setTimeout>;

  readonly formatCount = formatCount;
  readonly fieldPreview = FIELD_PREVIEW;

  ngOnInit() {
    // The shared list only counts once this visit has fetched it: before
    // that it may still hold another sign-in's sender IDs.
    this.broadcastService.getSenderIDs$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids) => {
        if (!this.store.senderIdsRequested) return;
        this.senderIDs = ids ?? [];
        this.checkSender();
      });

    // Once per visit: Back/Next recreate this step, the list stays loaded.
    // The flag is set only once the list has arrived, so a request cut short
    // by leaving the step, or one that failed, is tried again next time.
    if (!this.store.senderIdsRequested) {
      this.senderLoading = true;
      this.broadcastService
        .fetchSenderIDs()
        .pipe(
          finalize(() => (this.senderLoading = false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (ids) => {
            this.store.senderIdsRequested = true;
            this.senderIDs = ids ?? [];
            this.checkSender();
          },
          error: () => (this.senderFailed = true),
        });
    }
  }

  /**
   * A sender ID kept from a draft or earlier in the session that this
   * organisation no longer has is cleared, so the dropdown and what is sent
   * always agree.
   */
  private checkSender() {
    if (!this.store.senderIdsRequested) return;
    const current = this.store.senderIdSig();
    if (current && !this.senderIDs.some((sender) => sender.senderId === current)) {
      this.store.senderId = '';
    }
  }

  readonly fieldList = computed(() => {
    const phone = this.store.phoneIndex();
    // The phone column last: it is rarely what the message needs.
    const columns = this.store.columns();
    return [...columns.filter((c) => c.index !== phone), ...columns.filter((c) => c.index === phone)];
  });

  get visibleFields() {
    const list = this.fieldList();
    return this.showAllFields ? list : list.slice(0, FIELD_PREVIEW);
  }

  readonly usedSet = computed(() => new Set(this.store.usedFields().known));

  /** Fields in the message that are empty in some sending rows. */
  readonly emptyFields = computed(() => {
    const phoneField = this.store.phoneField();
    const empty = this.store.emptyByField();
    return this.store
      .usedFields()
      .known.filter((field) => field !== phoneField && (empty[field] ?? 0) > 0)
      .map((field) => ({ field, count: empty[field] }));
  });

  /** Plain-language notes on the message as a whole. */
  readonly notes = computed(() => {
    const notes: { tone: 'bad' | 'wait' | 'idle'; text: string; more?: string }[] = [];
    const unknown = this.store.usedFields().unknown;
    if (unknown.length) {
      notes.push({
        tone: 'bad',
        text: `${unknown.map((field) => this.token(field)).join(', ')} ${
          unknown.length === 1 ? 'isn’t a column' : 'aren’t columns'
        } in your sheet, so every message would show ${unknown.length === 1 ? 'it' : 'them'} as written.`,
        more: 'Check the spelling, or insert a field from the list.',
      });
    }
    const totals = this.store.totals();
    if (totals.unicode && totals.unicode < totals.messages) {
      const offender = this.firstUnicodeOffender();
      notes.push({
        tone: 'wait',
        text: `${formatCount(totals.unicode)} ${totals.unicode === 1 ? 'message switches' : 'messages switch'} to Unicode because of characters in ${
          totals.unicode === 1 ? 'its' : 'their'
        } values${offender ? `, like ${describeChar(offender)}` : ''}.`,
        more: 'A Unicode part holds 70 characters instead of 160, so those messages cost more.',
      });
    }
    return notes;
  });

  private firstUnicodeOffender(): string | null {
    for (const message of this.store.messages()) {
      if (message?.parts.offender) return message.parts.offender;
    }
    return null;
  }

  get canContinue(): boolean {
    return !!this.store.senderIdSig() && !!this.store.subjectSig().trim() && !!this.store.bodySig().trim();
  }

  get missing(): string {
    if (!this.store.senderIdSig()) return 'Choose a sender ID.';
    if (!this.store.subjectSig().trim()) return 'Name the broadcast.';
    if (!this.store.bodySig().trim()) return 'Write the message.';
    return '';
  }

  token(field: string): string {
    return `{{${field}}}`;
  }

  asValue(event: Event): string {
    return (event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
  }

  insertField(field: string) {
    const textarea = this.bodyArea?.nativeElement;
    if (textarea) this.appendAttribute(textarea, field);
  }

  appendAttribute(textarea: HTMLTextAreaElement, attribute: string) {
    const template = `{{${attribute}}}`;
    const body = this.store.messageBody || '';
    const start = textarea.selectionStart ?? body.length;
    const end = textarea.selectionEnd ?? body.length;
    this.store.messageBody = body.substring(0, start) + template + body.substring(end);

    const newPos = start + template.length;
    if (this.cursorTimeout) clearTimeout(this.cursorTimeout);
    this.cursorTimeout = setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
      this.cursorTimeout = undefined;
    }, 0);
  }

  /**
   * Removing a chip takes its button with it, so focus moves to the next
   * chip's remove button, or back to the message when none are left.
   */
  removeAttribute(attribute: string, index = 0) {
    this.store.messageBody = removeMessageAttribute(this.store.messageBody, attribute);
    if (this.cursorTimeout) clearTimeout(this.cursorTimeout);
    this.cursorTimeout = setTimeout(() => {
      this.cursorTimeout = undefined;
      const buttons = this.usedList?.nativeElement.querySelectorAll<HTMLButtonElement>('.used__remove');
      const next = buttons?.length ? buttons[Math.min(index, buttons.length - 1)] : null;
      if (next) next.focus();
      else this.bodyArea?.nativeElement.focus();
    }, 0);
  }

  insertLabel(field: string): string {
    return `Insert field ${field}${this.usedSet().has(field) ? ', already in the message' : ''}`;
  }

  setFallback(field: string, event: Event) {
    this.store.setFallback(field, this.asValue(event));
  }

  continue() {
    if (this.canContinue) {
      this.next.emit();
    } else {
      this.nameTouched = true;
    }
  }
}
